import { createPortal } from "react-dom";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import CloseIcon from "assets/icons/close.svg?react";
import { readFloat, writeFloat } from "utils/localStorageFloat";
import styles from "./styles.module.scss";

// Dwupanelowy slajd pełnoekranowy: po lewej podgląd dokumentu, po prawej panel
// akcji (formularz księgowania, lista certyfikatów do podpisu itp.). Proporcje
// podziału są przeciągane uchwytem i pamiętane w localStorage.

const DEFAULT_SPLIT = 0.55;
const MIN_SPLIT = 0.2;
const MAX_SPLIT = 0.82;

export interface IPreviewState {
  text: string;
  isError?: boolean;
  // Półprzezroczyste tło obszaru podglądu (np. do czasu RĘCZNEGO wywołania
  // podglądu — bez auto-doczytywania pliku, żeby nie obciążać serwera).
  dim?: boolean;
}

interface Props {
  /** Lewy panel — podgląd dokumentu (zwykle <PdfPreviewPane/>). */
  preview?: ReactNode;
  /** Komunikat wyświetlany zamiast podglądu (ładowanie / błąd / brak pliku). */
  previewState?: IPreviewState | null;
  title: string;
  /** Klucz localStorage z proporcją podziału (wspólny dla slajdów tego samego typu). */
  splitStorageKey: string;
  /** Klucz/etykieta wskaźnika DEV ToDo (domyślnie wyliczane z tytułu). */
  devTodoKey?: string;
  onClose: () => void;
  children: ReactNode;
}

const SplitSlide = ({
  preview,
  previewState,
  title,
  splitStorageKey,
  onClose,
  children,
}: Props) => {
  // ── Proporcje podziału ekranu ─────────────────────────────────────────────

  const [split, setSplit] = useState<number>(() =>
    readFloat(splitStorageKey, DEFAULT_SPLIT, MIN_SPLIT, MAX_SPLIT),
  );
  const splitRef = useRef(split);
  splitRef.current = split;
  const containerRef = useRef<HTMLDivElement>(null);

  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartSpl = useRef(DEFAULT_SPLIT);
  const [dragging, setDragging] = useState(false);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return;
    const totalW = containerRef.current.offsetWidth;
    if (totalW <= 0) return;
    const dx = e.clientX - dragStartX.current;
    const next = Math.min(
      MAX_SPLIT,
      Math.max(MIN_SPLIT, dragStartSpl.current + dx / totalW),
    );
    setSplit(next);
  }, []);

  const onMouseUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    setDragging(false);
    writeFloat(splitStorageKey, splitRef.current);
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  }, [onMouseMove, splitStorageKey]);

  const handleDividerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartSpl.current = splitRef.current;
    setDragging(true);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  useEffect(
    () => () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    },
    [onMouseMove, onMouseUp],
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return createPortal(
    <div
      ref={containerRef}
      className={`${styles.overlay}${
        dragging ? ` ${styles.overlayDragging}` : ""
      }`}
    >
      {/* Lewa strona — podgląd dokumentu */}
      <div
        className={styles.previewPanel}
        style={{ flexBasis: `${split * 100}%` }}
      >
        {previewState && (
          <div
            className={`${styles.previewState}${
              previewState.isError ? ` ${styles.previewStateError}` : ""
            }${previewState.dim ? ` ${styles.previewStateDim}` : ""}`}
          >
            {previewState.text}
          </div>
        )}
        {!previewState && preview}
      </div>

      {/* Uchwyt zmiany proporcji */}
      <div
        className={`${styles.divider}${
          dragging ? ` ${styles.dividerActive}` : ""
        }`}
        onMouseDown={handleDividerMouseDown}
        title="Przeciągnij, aby zmienić proporcje"
      >
        <div className={styles.dividerHandle} />
      </div>

      {/* Prawa strona — treść akcji */}
      <div className={styles.formPanel}>
        <div className={styles.formHeader}>
          <div className={styles.headerTitle}>
            <span>{title}</span>
          </div>
          <CloseIcon className={styles.closeIcon} onClick={onClose} />
        </div>
        <div className={styles.formContent}>{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export default SplitSlide;
